import React, { useState, useEffect, useRef } from 'react';
import { signInWithPhoneNumber, ConfirmationResult } from 'firebase/auth';
import { auth, setupRecaptcha } from '../../config/firebase';
import { FaPhone, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';

const PhoneVerification = ({ phoneNumber, onVerificationSuccess, onCancel }) => {
  const [step, setStep] = useState('send'); // 'send', 'verify', 'success'
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const recaptchaVerifierRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Format phone number to international format
  const formatPhoneNumber = (phone) => {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // If it starts with 0, remove it
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    
    // If it doesn't start with country code, add +91 for India
    if (!cleaned.startsWith('91') && cleaned.length === 10) {
      cleaned = '91' + cleaned;
    }
    
    // Add + prefix
    return '+' + cleaned;
  };

  // Cleanup countdown and reCAPTCHA on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      // Clean up reCAPTCHA verifier
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (error) {
          // Ignore errors during cleanup
        }
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  // Start countdown timer
  const startCountdown = (seconds = 60) => {
    setCountdown(seconds);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Send OTP
  const sendOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      
      // Clear existing reCAPTCHA verifier before creating a new one
      // This prevents "reCAPTCHA has already been rendered" error
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (clearError) {
          // Ignore clear errors if verifier was already cleared
          console.log('reCAPTCHA clear error (ignored):', clearError);
        }
        recaptchaVerifierRef.current = null;
      }
      
      // Remove the existing container element from DOM
      // This is crucial - Firebase reCAPTCHA renders into the DOM element
      // and we need a fresh element each time
      const existingContainer = document.getElementById('recaptcha-container');
      if (existingContainer) {
        existingContainer.remove();
      }
      
      // Create a fresh container element
      const recaptchaDiv = document.createElement('div');
      recaptchaDiv.id = 'recaptcha-container';
      recaptchaDiv.style.display = 'none';
      document.body.appendChild(recaptchaDiv);
      
      // Setup reCAPTCHA - create new verifier with fresh element
      recaptchaVerifierRef.current = setupRecaptcha('recaptcha-container');

      // Send OTP
      const confirmation = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        recaptchaVerifierRef.current
      );

      setConfirmationResult(confirmation);
      setStep('verify');
      startCountdown(60);
      toast.success('OTP sent successfully!');
    } catch (error) {
      console.error('Error sending OTP:', error);
      
      // Clean up reCAPTCHA on error - remove element and verifier
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (clearError) {
          // Ignore clear errors
          console.log('reCAPTCHA cleanup error (ignored):', clearError);
        }
        recaptchaVerifierRef.current = null;
      }
      
      // Remove container element on error
      const errorContainer = document.getElementById('recaptcha-container');
      if (errorContainer) {
        errorContainer.remove();
      }

      let errorMessage = 'Failed to send OTP. Please try again.';
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number format';
      } else if (error.code === 'auth/invalid-app-credential') {
        errorMessage = 'Firebase configuration error. Please check: 1) Domain is authorized in Firebase Console, 2) API key restrictions allow localhost, 3) reCAPTCHA Enterprise is enabled.';
        console.error('Firebase config issue. Check console for details.');
      } else if (error.code === 'auth/billing-not-enabled') {
        errorMessage = 'Phone verification requires billing to be enabled. Please contact support or enable billing in Firebase Console.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many OTP requests. Please wait 5 minutes before trying again. Firebase has rate limits to prevent abuse.';
        // Set a longer countdown (5 minutes) to prevent immediate retry
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
        startCountdown(300); // 5 minutes = 300 seconds
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = 'SMS quota exceeded. Please try again later.';
      } else if (error.message && error.message.includes('reCAPTCHA')) {
        errorMessage = 'reCAPTCHA error. Please refresh the page and try again.';
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    if (!confirmationResult) {
      toast.error('OTP session expired. Please request a new OTP.');
      return;
    }

    setLoading(true);
    try {
      const result = await confirmationResult.confirm(otp);
      const idToken = await result.user.getIdToken();

      // Clean up reCAPTCHA
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }

      // Clear countdown
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      setStep('success');
      toast.success('Phone number verified successfully!');
      
      // Call success callback with ID token
      if (onVerificationSuccess) {
        onVerificationSuccess(idToken, result.user);
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      let errorMessage = 'Invalid OTP. Please try again.';
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = 'Invalid OTP code';
      } else if (error.code === 'auth/code-expired') {
        errorMessage = 'OTP expired. Please request a new one.';
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const resendOTP = async () => {
    if (countdown > 0) {
      toast.info(`Please wait ${countdown} seconds before requesting a new OTP`);
      return;
    }
    await sendOTP();
  };

  // Handle cancel
  const handleCancel = () => {
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch (error) {
        // Ignore errors during cleanup
      }
      recaptchaVerifierRef.current = null;
    }
    // Remove container element
    const container = document.getElementById('recaptcha-container');
    if (container) {
      container.remove();
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    if (onCancel) {
      onCancel();
    }
  };

  // reCAPTCHA container (invisible)
  useEffect(() => {
    // Create a hidden div for reCAPTCHA if it doesn't exist
    if (!document.getElementById('recaptcha-container')) {
      const recaptchaDiv = document.createElement('div');
      recaptchaDiv.id = 'recaptcha-container';
      recaptchaDiv.style.display = 'none';
      document.body.appendChild(recaptchaDiv);
    }
    return () => {
      const recaptchaDiv = document.getElementById('recaptcha-container');
      if (recaptchaDiv) {
        recaptchaDiv.remove();
      }
    };
  }, []);

  if (step === 'success') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <FaCheckCircle className="text-green-600 text-2xl" />
          <div>
            <p className="font-semibold text-green-800">Phone Verified</p>
            <p className="text-sm text-green-600">{phoneNumber}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <FaPhone className="text-primary" />
          Verify Phone Number
        </h3>
        {onCancel && (
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimesCircle />
          </button>
        )}
      </div>

      {step === 'send' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <div className="flex gap-2">
              <input
                type="tel"
                value={phoneNumber}
                readOnly
                className="flex-1 px-4 py-2 border rounded-lg bg-gray-50"
              />
              <button
                onClick={sendOTP}
                disabled={loading}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send OTP'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'verify' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter 6-digit OTP
            </label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-center text-2xl tracking-widest"
              autoFocus
            />
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={verifyOTP}
              disabled={loading || otp.length !== 6}
              className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify OTP'
              )}
            </button>

            <button
              onClick={resendOTP}
              disabled={countdown > 0 || loading}
              className="text-primary hover:text-primary-dark text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneVerification;

